import { useMemo, useState } from "react";
import { Button } from "@heroui/react";
import {
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  MapPin,
  Plus,
  Truck,
} from "lucide-react";
import { PrintShipmentLabelButton } from "./components/print-shipment-label-button";
import {
  useCustomerOrdersV2,
  getRecipientFromOrder,
} from "@/api/use-customer-orders-v2";
import { type Order, type Shipment, ShipmentStatus } from "@/api.types.v2";
import { PickupBookingDetailSheet } from "./pickup-booking-detail-sheet";
import {
  formatDateInTz,
  formatSlotRangeInTz,
  todayIsoInTz,
  todayPlusDaysIsoInTz,
} from "@/lib/dispatch-time";
import {
  isPickupBookingActiveError,
  useMarkShipmentDroppedOff,
} from "@/api/use-mark-shipment-dropped-off";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface ShipmentWithOrder {
  shipment: Shipment;
  order: Order;
}

interface PickupGroup {
  key: string;
  // Phase 11a: management surface needs the actual booking ID, not a synthetic
  // (date,slot) key. One booking per group, one group per booking.
  bookingId: string;
  date: Date | null;
  scheduledDateIso: string | null;
  dateLabel: string;
  timeZone: string | null;
  timeSlot: { name: string; startTime: string; endTime: string } | null;
  shipments: ShipmentWithOrder[];
}

function formatDateLabel(
  scheduledDateIso: string | null,
  timeZone: string | null,
  t: (key: string) => string,
  locale?: string
): string {
  if (!scheduledDateIso) return t("pickup.date.unscheduled");
  // Fall back to browser tz when dispatch is missing — keeps old behavior for
  // any rows that pre-date the tz-annotated server response.
  const tz =
    timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";
  const date = scheduledDateIso.slice(0, 10);
  if (date === todayIsoInTz(tz)) return t("pickup.date.today");
  if (date === todayPlusDaysIsoInTz(tz, 1)) return t("pickup.date.tomorrow");
  return formatDateInTz(date, tz, "EEE, MMM d", locale);
}

function formatTimeSlot(
  slot: { name: string; startTime: string; endTime: string } | undefined,
  timeZone: string | null,
  isoDate: string | null,
  locale?: string
): string {
  if (!slot) return "";
  if (timeZone) {
    return formatSlotRangeInTz(slot, timeZone, isoDate ?? undefined, locale);
  }
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    if (locale?.toLowerCase().startsWith("zh")) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
      )}`;
    }
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return minutes === 0
      ? `${displayHours}${period}`
      : `${displayHours}:${minutes.toString().padStart(2, "0")}${period}`;
  };
  return `${formatTime(slot.startTime)} – ${formatTime(slot.endTime)}`;
}

function formatPackageInfo(shipment: Shipment): {
  weight: string;
  dimensions: string;
  count: number;
} {
  const packages = shipment.packages ?? [];
  if (packages.length === 0) return { weight: "", dimensions: "", count: 0 };

  const totalWeightGrams = packages.reduce(
    (sum, pkg) => sum + (pkg.weightGrams || 0),
    0
  );

  let weight = "";
  if (totalWeightGrams >= 1000) {
    weight = `${(totalWeightGrams / 1000).toFixed(1)} kg`;
  } else if (totalWeightGrams > 0) {
    weight = `${totalWeightGrams} g`;
  }

  const pkg = packages[0];
  let dimensions = "";
  if (pkg?.lengthCm && pkg.widthCm && pkg.heightCm) {
    dimensions = `${pkg.lengthCm} × ${pkg.widthCm} × ${pkg.heightCm} cm`;
  }

  return { weight, dimensions, count: packages.length };
}

function ShipmentRow({
  shipment,
  order,
  showDroppedOff = false,
}: ShipmentWithOrder & { showDroppedOff?: boolean }) {
  const { t } = useTranslation("app");
  const recipient = getRecipientFromOrder(order);
  const { weight, count } = formatPackageInfo(shipment);
  const labelUrl = shipment.packages?.find((p) => p.labelUrl)?.labelUrl;
  const { toast } = useToast();
  const markDroppedOff = useMarkShipmentDroppedOff({
    onSuccess: (data) => {
      toast({
        title: data.data.changed
          ? t("pickup.upcoming.markedDroppedOff")
          : t("pickup.upcoming.alreadyInTransit"),
        description: data.data.changed
          ? t("pickup.upcoming.droppedOffDescription", {
              shipmentNumber: shipment.shipmentNumber,
            })
          : t("pickup.upcoming.carrierScanned"),
      });
    },
    onError: (err) => {
      const desc = isPickupBookingActiveError(err)
        ? t("pickup.upcoming.scheduledPickupDropoff")
        : t("pickup.upcoming.updateFailedDescription");
      toast({
        title: t("pickup.upcoming.updateFailedTitle"),
        description: desc,
        variant: "destructive",
      });
    },
  });

  if (showDroppedOff) {
    const dropped = markDroppedOff.isSuccess;
    return (
      <div
        className={`grid grid-cols-[1fr_auto] md:grid-cols-[140px_1fr_auto] gap-3 md:gap-4 items-center px-4 md:px-5 py-3.5 border-b border-divider last:border-b-0 ${
          dropped ? "bg-success-50/60" : ""
        }`}
      >
        <div className="min-w-0 md:contents">
          <div className="font-mono font-semibold text-sm">
            SHP-{shipment.shipmentNumber}
          </div>
          <div className="min-w-0 mt-1 md:mt-0">
            <div className="text-sm text-default-700 truncate">
              {recipient?.name ?? t("pickup.upcoming.unknownRecipient")}
            </div>
            <div className="text-tiny text-default-500 mt-0.5">
              {t("pickup.common.packageCount", { count })}
              {weight && ` · ${weight}`}
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          {dropped ? (
            <span className="inline-flex items-center gap-1.5 text-tiny font-semibold text-success-700">
              <CheckCircle2 className="h-3.5 w-3.5" />{" "}
              {t("pickup.upcoming.droppedOffUpdated")}
            </span>
          ) : (
            <Button
              variant="bordered"
              size="sm"
              isLoading={markDroppedOff.isPending}
              isDisabled={markDroppedOff.isPending}
              startContent={
                !markDroppedOff.isPending ? (
                  <Check className="h-3.5 w-3.5" />
                ) : null
              }
              onPress={() => markDroppedOff.mutate({ shipmentId: shipment.id })}
            >
              {t("pickup.upcoming.droppedOffButton")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Pickup row — dashed dividers, compact
  return (
    <div className="grid grid-cols-[1fr_auto] md:grid-cols-[140px_1fr_auto_auto] gap-3 md:gap-4 items-center py-3 border-t border-dashed border-default-200 first:border-t-0">
      <div className="min-w-0 md:contents">
        <div className="font-mono font-semibold text-sm">
          SHP-{shipment.shipmentNumber}
        </div>
        <div className="min-w-0 mt-1 md:mt-0">
          <div className="text-sm text-default-700 truncate">
            {recipient?.name ?? t("pickup.upcoming.unknownRecipient")}
          </div>
          <div className="text-tiny text-default-500 mt-0.5 md:hidden">
            {count} {t("pickup.common.packageShort")}
            {weight && ` · ${weight}`}
          </div>
        </div>
      </div>
      <div className="hidden md:block text-tiny text-default-500 whitespace-nowrap">
        {count} {t("pickup.common.packageShort")}
        {weight && ` · ${weight}`}
      </div>
      <div>
        <PrintShipmentLabelButton
          shipmentId={shipment.id}
          disabled={!labelUrl}
          label={t("pickup.upcoming.print")}
          disabledTitle={t("pickup.upcoming.labelNotReady")}
          title={t("pickup.upcoming.printLabel")}
        />
      </div>
    </div>
  );
}

function PickupGroupSection({
  group,
  onOpen,
}: {
  group: PickupGroup;
  onOpen: (bookingId: string) => void;
}) {
  const { t, i18n } = useTranslation("app");
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const timeRange = formatTimeSlot(
    group.timeSlot ?? undefined,
    group.timeZone,
    group.scheduledDateIso,
    locale
  );
  const isToday = group.dateLabel === t("pickup.date.today");
  const isTomorrow = group.dateLabel === t("pickup.date.tomorrow");
  const urgent = isToday || isTomorrow;
  const shortBookingId = group.bookingId.slice(0, 7).toUpperCase();

  return (
    <div className="px-4 md:px-5 border-b border-divider last:border-b-0">
      {/* Booking header — clickable */}
      <button
        type="button"
        onClick={() => onOpen(group.bookingId)}
        className="w-full flex items-center gap-2 md:gap-3 py-3.5 text-left group/header"
        aria-label={t("pickup.upcoming.openDetail")}
      >
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 md:px-3 py-1 rounded-full text-tiny font-semibold whitespace-nowrap ${
            urgent
              ? "bg-primary-50 text-primary"
              : "bg-default-100 text-default-700"
          }`}
        >
          <Calendar className="h-3 w-3" />
          {group.dateLabel}
          {timeRange && ` · ${timeRange}`}
        </span>
        <span className="hidden md:inline font-mono text-tiny text-default-400">
          PB-{shortBookingId}
        </span>
        <span className="hidden md:inline text-xs text-default-500">
          {t("pickup.upcoming.standardPickup")}
        </span>
        <div className="flex-1" />
        <span className="text-xs text-default-500 whitespace-nowrap">
          {t("pickup.common.shipmentCount", {
            count: group.shipments.length,
          })}
        </span>
        <ChevronRight className="h-3.5 w-3.5 text-default-400 group-hover/header:text-default-700 transition-colors shrink-0" />
      </button>

      {/* Shipments under this booking — dashed dividers */}
      <div className="flex flex-col">
        {group.shipments.map(({ shipment, order }) => (
          <ShipmentRow key={shipment.id} shipment={shipment} order={order} />
        ))}
      </div>

      {/* Inline attach */}
      <button
        type="button"
        onClick={() => onOpen(group.bookingId)}
        className="w-full py-2.5 mb-2 text-xs font-medium text-primary hover:underline border-t border-dashed border-default-200 inline-flex items-center justify-center gap-1.5"
      >
        <Plus className="h-3 w-3" /> {t("pickup.upcoming.attachAnother")}
      </button>
    </div>
  );
}

export function UpcomingShipments() {
  const { t, i18n } = useTranslation("app");
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const { data: response, isLoading } = useCustomerOrdersV2({
    page: 1,
    limit: 100,
  });

  const { pickupGroups, dropoffShipments } = useMemo(() => {
    const orders = response?.data ?? [];
    const pickups: ShipmentWithOrder[] = [];
    const dropoffs: ShipmentWithOrder[] = [];

    for (const order of orders) {
      for (const shipment of order.shipments ?? []) {
        if (shipment.status !== ShipmentStatus.LABEL_READY) continue;
        if (shipment.cancellationRequestedAt) continue;

        const item: ShipmentWithOrder = { shipment, order };

        // Pickup booking attached → we collect from sender. Otherwise the
        // sender is responsible for dropping off (to us or to a carrier).
        if (shipment.pickupBookingId != null) {
          pickups.push(item);
        } else {
          dropoffs.push(item);
        }
      }
    }

    const groupsMap = new Map<string, PickupGroup>();

    for (const item of pickups) {
      // Phase 11a: group by bookingId so each card maps to exactly one
      // PickupBooking — required for the click → detail-sheet flow. Two
      // bookings on the same date+slot but different addresses now stay
      // separate cards (previously merged).
      const bookingId = item.shipment.pickupBookingId;
      if (!bookingId) continue;
      const booking = item.shipment.pickupBooking ?? null;
      const pickupDate = booking?.scheduledDate
        ? new Date(booking.scheduledDate)
        : null;
      const pickupSlot = booking?.slot ?? null;
      const tz = booking?.dispatchCenter?.timeZone ?? null;

      if (!groupsMap.has(bookingId)) {
        groupsMap.set(bookingId, {
          key: bookingId,
          bookingId,
          date: pickupDate,
          scheduledDateIso: booking?.scheduledDate ?? null,
          dateLabel: formatDateLabel(
            booking?.scheduledDate ?? null,
            tz,
            t,
            locale
          ),
          timeZone: tz,
          timeSlot: pickupSlot ?? null,
          shipments: [],
        });
      }
      groupsMap.get(bookingId)!.shipments.push(item);
    }

    const sortedGroups = Array.from(groupsMap.values()).sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return a.date.getTime() - b.date.getTime();
    });

    return {
      pickupGroups: sortedGroups,
      dropoffShipments: dropoffs,
    };
  }, [response?.data, t, locale]);

  const [pickupsExpanded, setPickupsExpanded] = useState(true);
  const [dropoffsExpanded, setDropoffsExpanded] = useState(true);
  const [openBookingId, setOpenBookingId] = useState<string | null>(null);

  if (isLoading) return null;
  if (pickupGroups.length === 0 && dropoffShipments.length === 0) return null;

  const totalPickups = pickupGroups.reduce(
    (sum, g) => sum + g.shipments.length,
    0
  );

  return (
    <div className="space-y-4">
      {/* Pickup bookings card */}
      {totalPickups > 0 && (
        <div className="bg-content1 rounded-large shadow-small overflow-hidden">
          <div className="px-5 py-4 flex items-center gap-3 border-b border-divider">
            <div className="w-9 h-9 rounded-medium bg-primary-50 text-primary grid place-items-center">
              <Truck className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="text-[15px] font-semibold">
                {t("pickup.upcoming.pickupBookings")}
              </div>
              <div className="text-tiny text-default-500">
                {t("pickup.upcoming.upcomingSummary", {
                  bookings: pickupGroups.length,
                  shipments: totalPickups,
                  count: totalPickups,
                })}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPickupsExpanded((prev) => !prev)}
              aria-label={
                pickupsExpanded
                  ? t("pickup.upcoming.collapse")
                  : t("pickup.upcoming.expand")
              }
              className="text-default-400 hover:text-default-700 p-1"
            >
              {pickupsExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </div>

          {pickupsExpanded && (
            <div>
              {pickupGroups.map((group) => (
                <PickupGroupSection
                  key={group.key}
                  group={group}
                  onOpen={setOpenBookingId}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Ready for drop off */}
      {dropoffShipments.length > 0 && (
        <div className="bg-content1 rounded-large shadow-small overflow-hidden">
          <button
            type="button"
            onClick={() => setDropoffsExpanded((prev) => !prev)}
            className={`w-full px-5 py-4 flex items-center gap-3 text-left ${
              dropoffsExpanded ? "border-b border-divider" : ""
            }`}
          >
            <div className="w-9 h-9 rounded-medium bg-default-100 text-default-700 grid place-items-center">
              <MapPin className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="text-[15px] font-semibold">
                {t("pickup.upcoming.readyForDropoff")}
              </div>
              <div className="text-tiny text-default-500">
                {t("pickup.upcoming.dropoffSummary", {
                  count: dropoffShipments.length,
                })}
              </div>
            </div>
            {dropoffsExpanded ? (
              <ChevronUp className="h-4 w-4 text-default-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-default-400" />
            )}
          </button>
          {dropoffsExpanded && (
            <div>
              {dropoffShipments.map(({ shipment, order }) => (
                <ShipmentRow
                  key={shipment.id}
                  shipment={shipment}
                  order={order}
                  showDroppedOff
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Phase 11a: detail sheet — opens when a pickup card is clicked. */}
      <PickupBookingDetailSheet
        bookingId={openBookingId}
        onClose={() => setOpenBookingId(null)}
      />
      {/* Phase 11b: schedule-from-scratch wizard. `key` forces a fresh
          mount each time the user opens it — wizard state resets cleanly
          without setState-in-effect. */}
    </div>
  );
}
